import { useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'license-dashboard.theme';

function getStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  return 'dark';
}

function getEffectiveTheme(theme: Theme): 'dark' | 'light' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [resolved, setResolved] = useState<'dark' | 'light'>(() =>
    getEffectiveTheme(getStoredTheme())
  );

  useEffect(() => {
    const effective = getEffectiveTheme(theme);
    setResolved(effective);
    if (effective === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setResolved(e.matches ? 'dark' : 'light');
      if (e.matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  const set = (t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t);
    setTheme(t);
  };

  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    set(next);
  };

  return { theme, resolved, setTheme: set, toggle };
}
